import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, CircularProgress, Paper, Button, IconButton, Avatar, TextField, InputAdornment, Snackbar, Alert } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FilterSidebar from '../../components/FilterSidebar';
import './YouTube.css';
import useSWR from 'swr';
import API_ENDPOINTS from '../../apis/endpoints';

const formatNumber = (num) => {
    if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
};

// Helper function to map API response to component structure
const mapApiResponse = (data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map(item => ({
        id: item.account_id,
        name: item.profile?.title || item.profile?.name || 'Unknown',
        handle: item.profile?.username || '',
        img: item.profile?.image_url || 'https://via.placeholder.com/150',
        followers: item.engagement?.followers || 0,
        engagementRate: 0, // Will calculate later
        uploads: item.engagement?.contents || 0,
        platform: item.platform,
        category: item.profile?.demographics?.category || 'General',
        country: item.profile?.location?.country || 'Unknown',
        ...item.profile
    }));
};

const YouTube = () => {
    const [query, setQuery] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filters, setFilters] = useState({});
    const [rowsPerPage] = useState(10);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [cursor, setCursor] = useState(null); // Current cursor for pagination
    const [cursorHistory, setCursorHistory] = useState([]); // Track cursor history for back navigation

    // Build query parameters
    const buildQueryParams = () => {
        const params = new URLSearchParams();
        params.append('platform', 'youtube');
        
        // Use limit from filters if provided, otherwise use rowsPerPage (default 10)
        // Limit must be an integer between 1 and 500
        let limitValue = rowsPerPage; // default 10
        if (filters.limit && filters.limit !== '' && filters.limit !== null && filters.limit !== undefined) {
            const parsedLimit = parseInt(filters.limit, 10);
            if (!isNaN(parsedLimit) && parsedLimit >= 1) {
                limitValue = Math.min(parsedLimit, 500); // Cap at 500
            }
        }
        params.append('limit', limitValue.toString());

        // Add cursor if available (for pagination)
        if (cursor) {
            params.append('cursor', cursor);
        }

        // Add search query using title__contains parameter
        if (searchQuery) {
            params.append('title__contains', searchQuery);
        }

        // Account type filter (comma-separated)
        if (Array.isArray(filters.accountType) && filters.accountType.length > 0) {
            params.append('account_type__in', filters.accountType.join(','));
        }

        // Location country filter (single select)
        if (filters.locationCountry && typeof filters.locationCountry === 'string') {
            params.append('location_country__in', filters.locationCountry);
        }

        // Handle min__followers parameter
        if (filters.followers && filters.followers !== '' && filters.followers !== null && filters.followers !== undefined) {
            params.append('min__followers', filters.followers);
        }

        // Handle min__contents parameter
        if (filters.contentCount && filters.contentCount !== '' && filters.contentCount !== null && filters.contentCount !== undefined) {
            params.append('min__contents', filters.contentCount);
        }

        // Handle min__views parameter
        if (filters.viewCount && filters.viewCount !== '' && filters.viewCount !== null && filters.viewCount !== undefined) {
            params.append('min__views', filters.viewCount);
        }

        // Add other filters if needed
        Object.keys(filters).forEach(key => {
            if (key !== 'followers' && key !== 'limit' && key !== 'contentCount' && key !== 'viewCount' && filters[key] !== '' && filters[key] !== null && filters[key] !== undefined) {
                // Skip arrays and objects that are already handled above
                if (!Array.isArray(filters[key]) && typeof filters[key] !== 'object') {
                    params.append(key, filters[key]);
                }
            }
        });

        return params.toString();
    };

    const queryString = buildQueryParams();
    const { data, error, isLoading } = useSWR(
        queryString ? `${API_ENDPOINTS.BRANDCONNECT_ACCOUNTS}?${queryString}` : null
    );

    // Transform API response
    // The fetcher returns data.data, so we get { meta: {...}, data: [...], pagination: {...} }
    const apiData = data || {};
    const creators = apiData.data ? mapApiResponse(apiData.data) : [];
    const totalCount = apiData.meta?.total_count || apiData.pagination?.total_count || 0;
    const nextCursor = apiData.pagination?.cursor || null;
    const hasMore = apiData.pagination?.has_more || false;

    useEffect(() => {
        if (error) {
            setOpenSnackbar(true);
        }
    }, [error]);

    const handleSearch = () => {
        setSearchQuery(query);
        setCursor(null);
        setCursorHistory([]);
    };

    const handleFilterApply = (newFilters) => {
        setFilters(newFilters);
        setCursor(null);
        setCursorHistory([]);
    };

    const handleNextPage = () => {
        if (nextCursor) {
            setCursorHistory(prev => [...prev, cursor]);
            setCursor(nextCursor);
        }
    };

    const handlePreviousPage = () => {
        if (cursorHistory.length > 0) {
            const previousCursors = [...cursorHistory];
            const previousCursor = previousCursors.pop();
            setCursorHistory(previousCursors);
            setCursor(previousCursor);
        } else {
            // Go back to first page
            setCursor(null);
            setCursorHistory([]);
        }
    };

    const canGoNext = hasMore && nextCursor !== null;
    const canGoPrevious = cursorHistory.length > 0 || cursor !== null;

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    return (
        <div className="min-h-screen bg-[#F5F7FB] px-4 sm:px-6 py-6 sm:py-8">
            <div className="max-w-6xl mx-auto bg-white rounded-3xl shadow-sm px-4 sm:px-8 py-6 sm:py-8 space-y-6">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="#FF0000"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ marginTop: '-4px' }}
                    >
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                    <Typography variant="featureAccent" gutterBottom className="text-left">
                        YouTube Creators
                    </Typography>
                </Box>

                {/* Search Bar and Filter */}
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex-1 flex gap-2">
                        <Box sx={{ position: 'relative', flex: 1 }}>
                            <TextField
                                fullWidth
                                placeholder="Search by title..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSearch();
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: 'grey.500' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        bgcolor: 'white',
                                        '& fieldset': {
                                            borderColor: '#e0e0e0',
                                        },
                                    },
                                }}
                            />
                        </Box>
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            sx={{
                                bgcolor: '#2D2D5F',
                                color: 'white',
                                borderRadius: '12px',
                                px: 3,
                                py: 1.5,
                                textTransform: 'none',
                                fontSize: '0.875rem',
                                minWidth: '100px',
                                '&:hover': {
                                    bgcolor: '#1a1a3a',
                                },
                            }}
                            startIcon={<SearchIcon />}
                        >
                            Search
                        </Button>
                    </div>
                    <Button
                        variant="outlined"
                        onClick={() => setIsFilterOpen(true)}
                        sx={{
                            px: 3,
                            py: 1.5,
                            borderColor: '#e0e0e0',
                            color: '#666',
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: '#bdbdbd',
                                backgroundColor: '#f5f5f5',
                            },
                        }}
                        startIcon={
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <line x1="4" y1="21" x2="4" y2="14"></line>
                                <line x1="4" y1="10" x2="4" y2="3"></line>
                                <line x1="12" y1="21" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12" y2="3"></line>
                                <line x1="20" y1="21" x2="20" y2="16"></line>
                                <line x1="20" y1="12" x2="20" y2="3"></line>
                                <line x1="1" y1="14" x2="7" y2="14"></line>
                                <line x1="9" y1="8" x2="15" y2="8"></line>
                                <line x1="17" y1="16" x2="23" y2="16"></line>
                            </svg>
                        }
                    >
                        Filters
                    </Button>
                </div>

                {/* Results Count & Pagination */}
                {totalCount > 0 && (
                    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between text-sm text-gray-600 mt-2 mb-4">
                        <span className="order-2 md:order-1">
                            Showing <span className="font-semibold text-black">{creators.length}</span> of <span className="font-semibold text-black">{totalCount}</span> Results
                        </span>
                        <div className="flex items-center gap-2 order-1 md:order-2">
                            <IconButton
                                onClick={handlePreviousPage}
                                disabled={!canGoPrevious}
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                    width: '32px',
                                    height: '32px',
                                    color: canGoPrevious ? '#666' : '#ccc',
                                    '&:hover': {
                                        backgroundColor: canGoPrevious ? '#f5f5f5' : 'transparent',
                                        borderColor: canGoPrevious ? '#bdbdbd' : '#e0e0e0',
                                    },
                                    '&.Mui-disabled': {
                                        borderColor: '#e0e0e0',
                                        color: '#ccc',
                                    },
                                }}
                            >
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                onClick={handleNextPage}
                                disabled={!canGoNext}
                                sx={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                    width: '32px',
                                    height: '32px',
                                    color: canGoNext ? '#666' : '#ccc',
                                    '&:hover': {
                                        backgroundColor: canGoNext ? '#f5f5f5' : 'transparent',
                                        borderColor: canGoNext ? '#bdbdbd' : '#e0e0e0',
                                    },
                                    '&.Mui-disabled': {
                                        borderColor: '#e0e0e0',
                                        color: '#ccc',
                                    },
                                }}
                            >
                                <ArrowForwardIcon fontSize="small" />
                            </IconButton>
                        </div>
                    </div>
                )}

                {/* Filter Sidebar */}
                <FilterSidebar
                    isOpen={isFilterOpen}
                    onClose={() => setIsFilterOpen(false)}
                    onApply={handleFilterApply}
                    initialFilters={filters}
                />

                {/* List View */}
                {isLoading ? (
                    <Box className="loader-container">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box className="youtube-results">
                        {creators.length > 0 ? (
                            <>
                                {creators.map((creator) => (
                                    <Paper
                                        key={creator.id}
                                        className="creator-card"
                                        sx={{
                                            p: 2,
                                            mb: 2,
                                            display: 'flex',
                                            flexDirection: { xs: 'column', md: 'row' },
                                            alignItems: { xs: 'flex-start', md: 'center' },
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                            '&:hover': {
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                            },
                                            transition: 'box-shadow 0.2s',
                                        }}
                                    >
                                        {/* Left Section: Checkbox & Profile */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                width: { xs: '100%', md: '33%' },
                                            }}
                                        >
                                            <Avatar
                                                src={creator.img}
                                                alt={creator.name}
                                                sx={{ width: 48, height: 48 }}
                                            />
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                    {creator.name}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#666',
                                                        display: 'block',
                                                        maxWidth: { xs: '100%', md: '220px' },
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                    title={creator.handle}
                                                >
                                                    {creator.handle}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Middle Section: Stats */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: { xs: 'space-between', md: 'flex-start' },
                                                gap: { xs: 2, md: 4 },
                                                width: { xs: '100%', md: '33%' },
                                                px: { xs: 0, md: 2 },
                                            }}
                                        >
                                            <Box sx={{ textAlign: { xs: 'left', md: 'left' } }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: '#999',
                                                        textTransform: 'uppercase',
                                                        fontWeight: 500,
                                                        fontSize: '0.7rem',
                                                    }}
                                                >
                                                    Subscribers
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                                                    {formatNumber(creator.followers)}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ textAlign: { xs: 'left', md: 'left' } }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: '#999',
                                                        textTransform: 'uppercase',
                                                        fontWeight: 500,
                                                        fontSize: '0.7rem',
                                                    }}
                                                >
                                                    Uploads
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                                                    {formatNumber(creator.uploads)}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Right Section: Action */}
                                        <Box
                                            sx={{
                                                width: { xs: '100%', md: '16%' },
                                                display: 'flex',
                                                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                            }}
                                        >
                                            <Button
                                                component={Link}
                                                to={`/dashboard/channel/${creator.id}`}
                                                variant="outlined"
                                                sx={{
                                                    px: 3,
                                                    py: 1,
                                                    borderRadius: '20px',
                                                    borderColor: '#e0e0e0',
                                                    color: '#666',
                                                    textTransform: 'none',
                                                    fontSize: '0.875rem',
                                                    width: { xs: 'auto', md: 'auto' },
                                                    '&:hover': {
                                                        borderColor: '#bdbdbd',
                                                        backgroundColor: '#f5f5f5',
                                                    },
                                                }}
                                            >
                                                View Profile
                                            </Button>
                                        </Box>
                                    </Paper>
                                ))}
                            </>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 6 }}>
                                <Typography variant="body1" sx={{ color: '#666' }}>
                                    No creators found.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}

                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity="error"
                        sx={{ width: "100%" }}
                    >
                        {error?.message
                            ? error?.message
                            : "An error occurred. Please try again later."}
                    </Alert>
                </Snackbar>
            </div>
        </div>
    );
};

export default YouTube;

